<template>
  <div class="app-mobile-field">
    <div v-if="props.label" class="field-label">
      <v-icon v-if="props.labelIcon" size="18">{{ props.labelIcon }}</v-icon>
      <span>{{ props.label }}</span>
    </div>

    <v-phone-input
      ref="phoneInputRef"
      :model-value="inputValue"
      :class="fieldClass"
      :variant="props.variant"
      :flat="props.flat"
      :default-country="props.defaultCountry"
      :invalid-message="props.invalidMessage"
      label=""
      hide-details
      v-bind="fieldAttrs"
      @update:model-value="handleInputValue"
      @update:phone-object="handlePhoneObject"
    />
    <span class="error-message" v-if="showInvalidMessage">{{ props.invalidMessage }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs, watch } from "vue";
import type { ParsedPhoneNumber } from "awesome-phonenumber";

defineOptions({
  inheritAttrs: false,
});

const model = defineModel<string | null>();

const props = withDefaults(
  defineProps<{
    variant?:
      | "solo"
      | "filled"
      | "outlined"
      | "plain"
      | "solo-filled"
      | "solo-inverted"
      | "underlined";
    flat?: boolean;
    hideDetails?: boolean | "auto";
    label?: string;
    labelIcon?: string;
    defaultCountry?: string;
    invalidMessage?: string;
  }>(),
  {
    variant: "solo",
    flat: true,
    hideDetails: "auto",
    label: "",
    labelIcon: "",
    defaultCountry: "US",
    invalidMessage: "Enter a valid phone number",
  }
);

const attrs = useAttrs();
const fieldClass = computed(() => ["app-mobile-field__input", attrs.class]);
const inputValue = ref("");
const phoneInputRef = ref<{
  isValid?: boolean | null;
  errorMessages?: string[] | null;
} | null>(null);
const fieldAttrs = computed(() => {
  const { class: _class, ...rest } = attrs as Record<string, unknown>;
  return rest;
});

watch(
  model,
  (value) => {
    inputValue.value = String(value || "");
  },
  { immediate: true, deep: true },
);

function handleInputValue(value: string | null) {
  inputValue.value = String(value || "");
}

function handlePhoneObject(phoneObject: ParsedPhoneNumber | null) {
  if (!phoneObject) {
    model.value = String(inputValue.value || "").trim();
    return;
  }

  const extension = phoneObject.countryCode ? `+${phoneObject.countryCode}` : "";
  const phone =
    phoneObject.number?.significant?.trim() ||
    phoneObject.number?.input?.replace(/\s+/g, "").trim() ||
    inputValue.value.trim();

  model.value = [extension, phone].filter(Boolean).join(" ").trim();
}

const showInvalidMessage = computed(() => {
  const attrErrorMessages =
    (fieldAttrs.value as Record<string, unknown>)["error-messages"] ??
    (fieldAttrs.value as Record<string, unknown>).errorMessages;

  const hasAttrErrors = Array.isArray(attrErrorMessages)
    ? attrErrorMessages.length > 0
    : Boolean(attrErrorMessages);

  return hasAttrErrors || phoneInputRef.value?.isValid === false;
});
</script>

<style scoped>
.app-mobile-field {
  width: 100%;
}

.field-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-subtitle);
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 6px;
  padding: 0 10px;
}

.app-mobile-field__input {
  --v-input-control-height: 48px;
  --v-input-padding-top: 0px;
  --v-field-padding-top: 0px;
  --v-field-padding-bottom: 0px;
  --v-field-input-padding-top: 0px;
  --v-field-input-padding-bottom: 0px;
  --v-field-padding-start: 12px;
  --v-field-padding-end: 12px;
}

.app-mobile-field__input :deep(.v-field) {
  border: 1px solid #eaeaea;
}

.app-mobile-field__input :deep(.v-field__input) {
  font-size: 0.875rem;
}

.app-mobile-field__input :deep(.v-input--density-default .v-field--variant-outlined),
.app-mobile-field__input :deep(.v-input--density-default .v-field--single-line),
.app-mobile-field__input :deep(.v-input--density-default .v-field--no-label) {
  --v-field-padding-bottom: 0px !important;
}

.app-mobile-field__input :deep(.v-input--density-default .v-field--variant-solo),
.app-mobile-field__input :deep(.v-input--density-default .v-field--variant-solo-inverted),
.app-mobile-field__input :deep(.v-input--density-default .v-field--variant-solo-filled),
.app-mobile-field__input :deep(.v-input--density-default .v-field--variant-filled) {
  --v-input-control-height: 48px;
  --v-field-padding-bottom: 0px !important;
}

.error-message
{
  color: #dc2626 !important;
  font-size: 13px !important;
  padding-left: 16px;
}
</style>
